import Script from 'next/script';

interface StructuredDataProps {
  data: Record<string, any>;
}

/**
 * StructuredData component for adding JSON-LD structured data to pages
 * This helps search engines understand your content better
 */
export default function StructuredData({ data }: StructuredDataProps) {
  return (
    <Script
      id="structured-data"
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

/**
 * Organization structured data for Eshhar Real Estate
 */
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Eshhar Real Estate',
  alternateName: 'Eshhar',
  url: 'https://eshharrealestate.com',
  logo: 'https://eshharrealestate.com/logo.png',
  description:
    'CAFM system, property management, and asset lifecycle software experts headquartered in Qatar since 2011, delivering facility maintenance, MEP, HVAC, and compliance-ready services.',
  foundingDate: '2011',
  legalName: 'Eshhar Real Estate',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+974-44111144',
    contactType: 'Customer Service',
    areaServed: ['Qatar', 'Gulf Cooperation Council', 'Canada'],
    availableLanguage: ['English', 'Arabic']
  },
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Zone 25, Street 330, 7th Floor, Buzwair Building No. 53',
    addressLocality: 'Doha',
    addressCountry: 'QA',
    postalCode: '12221'
  },
  sameAs: [
    // Add social media links when available
  ],
  areaServed: [
    {
      '@type': 'Country',
      name: 'Qatar'
    },
    {
      '@type': 'Place',
      name: 'Gulf Cooperation Council'
    },
    {
      '@type': 'Country',
      name: 'Canada'
    }
  ],
  hasCredential: {
    '@type': 'EducationalOccupationalCredential',
    credentialCategory: 'Registration',
    name: 'Commercial Registration No. 94697',
    recognizedBy: {
      '@type': 'Organization',
      name: 'Qatar Ministry of Commerce'
    }
  }
};

/**
 * Local Business structured data
 */
export const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: 'Eshhar Real Estate',
  image: 'https://eshharrealestate.com/logo.png',
  '@id': 'https://eshharrealestate.com/#organization',
  url: 'https://eshharrealestate.com',
  telephone: '+974-44111144',
  priceRange: '$$',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Zone 25, Street 330, 7th Floor, Buzwair Building No. 53',
    addressLocality: 'Doha',
    addressRegion: 'Doha',
    postalCode: '12221',
    addressCountry: 'QA'
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 25.276987, // Update with actual coordinates
    longitude: 51.520008
  },
  openingHoursSpecification: {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: [
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Sunday'
    ],
    opens: '08:00',
    closes: '17:00'
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.8',
    reviewCount: '150',
    bestRating: '5',
    worstRating: '1'
  }
};

/**
 * Service schema for the main services offered
 */
export const servicesSchema = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Eshhar Real Estate Services',
  description:
    'Comprehensive CAFM, asset management software, and facility services for Qatar organizations with GCC-ready capabilities.',
  itemListElement: [
    {
      '@type': 'Service',
      position: 1,
      name: 'Facilities and Property Management',
      description:
        'Professional CAFM-enabled property management including tenant relations, work order automation, maintenance coordination, and administrative tasks.',
      provider: {
        '@type': 'Organization',
        name: 'Eshhar Real Estate'
      }
    },
    {
      '@type': 'Service',
      position: 2,
      name: 'MEP Services',
      description:
        'Mechanical, Electrical, and Plumbing services including HVAC, power distribution, plumbing, and fire safety systems with predictive maintenance insights.',
      provider: {
        '@type': 'Organization',
        name: 'Eshhar Real Estate'
      }
    },
    {
      '@type': 'Service',
      position: 3,
      name: 'HVAC Services',
      description:
        'Installation, repair, and maintenance of energy-efficient heating, ventilation, and air conditioning systems tied directly into our CAFM dashboards.',
      provider: {
        '@type': 'Organization',
        name: 'Eshhar Real Estate'
      }
    },
    {
      '@type': 'Service',
      position: 4,
      name: 'Renovation Services',
      description: 'Construction, renovation, painting, and waterproofing for residential and commercial properties.',
      provider: {
        '@type': 'Organization',
        name: 'Eshhar Real Estate'
      }
    },
    {
      '@type': 'Service',
      position: 5,
      name: 'Fit-out Services',
      description:
        'Interior construction and finishing services tailored to operational needs, asset registers, and compliance specifications.',
      provider: {
        '@type': 'Organization',
        name: 'Eshhar Real Estate'
      }
    },
    {
      '@type': 'Service',
      position: 6,
      name: 'Cleaning & Pest Control Services',
      description:
        'Eco-friendly cleaning solutions and comprehensive pest control for homes, offices, and sensitive spaces, fully tracked in our CAFM system.',
      provider: {
        '@type': 'Organization',
        name: 'Eshhar Real Estate'
      }
    },
    {
      '@type': 'Service',
      position: 7,
      name: 'CAFM & Asset Management Software',
      description:
        'Cloud-based CAFM platform for Qatar portfolios with GCC scalability, enabling asset lifecycle planning, compliance workflows, and bilingual dashboards for remote teams including Canadian stakeholders.',
      provider: {
        '@type': 'Organization',
        name: 'Eshhar Real Estate'
      }
    }
  ]
};

/**
 * Breadcrumb schema for navigation
 */
export const createBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url
  }))
});

/**
 * Website schema
 */
export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Eshhar Real Estate',
  url: 'https://eshharrealestate.com',
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: 'https://eshharrealestate.com/?search={search_term_string}'
    },
    'query-input': 'required name=search_term_string'
  }
};

/**
 * FAQ Schema for common questions
 */
export const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Is there a CAFM system in Qatar that combines facility and asset management software?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Yes. Eshhar Real Estate delivers a Qatar-built CAFM system that unifies work order automation, asset registers, maintenance scheduling, and compliance-ready reporting—trusted by government, commercial, and enterprise portfolios.',
      },
    },
    {
      '@type': 'Question',
      name: 'What services does Eshhar Real Estate offer?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Eshhar Real Estate offers comprehensive property and facility management services including CAFM software, MEP services, HVAC installation and maintenance, property renovation, fit-out services, cleaning services, pest control, and complete facility management solutions in Qatar.',
      },
    },
    {
      '@type': 'Question',
      name: 'Where is Eshhar Real Estate located?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Eshhar Real Estate is located at Zone 25, Street 330, 7th Floor, Buzwair Building No. 53, Doha, Qatar. We can be reached at +974 44111144 or info@eshharrealestate.com.',
      },
    },
    {
      '@type': 'Question',
      name: 'How long has Eshhar Real Estate been in business?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Eshhar Real Estate has been serving Qatar since 2011, providing excellence in real estate, MEP, HVAC, property management, and facility services. We are a proud member of the Eshhar Group and certified by the Ministry of Commerce (CR No. 94697).',
      },
    },
    {
      '@type': 'Question',
      name: 'What areas does Eshhar Real Estate serve?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Eshhar Real Estate provides property management and facility services throughout Qatar, including Doha and surrounding areas, while onboarding remote GCC and Canadian portfolios via our CAFM platform. We work with residential, commercial, and government properties.',
      },
    },
    {
      '@type': 'Question',
      name: 'How can I request a quote for property management services?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You can request a free quote by contacting us at +974 44111144, emailing info@eshharrealestate.com, or filling out our inquiry form on our website. Our team will provide a customized quote based on your property management needs.',
      },
    },
    {
      '@type': 'Question',
      name: 'Does Eshhar Real Estate provide 24/7 maintenance services?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, Eshhar Real Estate offers 24/7 support for emergency maintenance and facility management services. Our CAFM system routes urgent work orders to the right teams in minutes, keeping Qatar operations and remote stakeholders aligned.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do you support facility management teams in Canada?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'We are seeing strong Canadian demand for Qatar-grade facility management processes. Our CAFM software supports bilingual dashboards, Canadian compliance data fields, and collaborative reporting so overseas stakeholders stay in sync with on-ground teams in Doha.',
      },
    },
  ],
};

